import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppFab } from "../components/WhatsAppFab";
import { TowersShowcase } from "../components/TowersShowcase";
import { BackstageGallery } from "../components/BackstageGallery";
import { useLang } from "../context/LanguageContext";
import { useSeo } from "../lib/useSeo";

const HERO_COPY = {
  pt: {
    overline: "Infra-estruturas",
    title: "Infraestruturas para Torres de Telecomunicações",
    subtitle:
      "Construímos a base física que sustenta as redes de TVCabSat — torres, mastros, fundações e ancoragens — preparadas para o clima, vento e ambiente operacional de Angola.",
    badge: "Engenharia de obra · Memória de cálculo · Cobertura nacional",
  },
  en: {
    overline: "Infrastructure",
    title: "Infrastructure for Telecom Towers",
    subtitle:
      "We build the physical foundation that supports TVCabSat networks — towers, masts, foundations and anchors — engineered for Angolan climate, wind and operational environment.",
    badge: "Site engineering · Structural calculation · Nationwide coverage",
  },
  es: {
    overline: "Infraestructuras",
    title: "Infraestructuras para Torres de Telecomunicaciones",
    subtitle:
      "Construimos la base física que sostiene las redes de TVCabSat — torres, mástiles, cimientos y anclajes — preparadas para el clima, viento y ambiente operacional de Angola.",
    badge:
      "Ingeniería de obra · Memoria de cálculo · Cobertura nacional",
  },
};

const SEO = {
  pt: {
    title: "Infra-estruturas · Torres e Mastros — TVCabSat Angola",
    description:
      "Infra-estruturas para telecomunicações em Angola: torres autoportantes e estaiadas e mastros de 6 a 30 metros, fundações em betão armado e instalação de antenas.",
  },
  en: {
    title: "Infrastructure · Towers & Masts — TVCabSat Angola",
    description:
      "Telecom infrastructure in Angola: self-supporting and guyed towers, masts from 6 to 30 m, reinforced concrete foundations and antenna installation.",
  },
  es: {
    title: "Infraestructura · Torres y Mástiles — TVCabSat Angola",
    description:
      "Infraestructura de telecomunicaciones en Angola: torres autoportantes y con vientos, mástiles de 6 a 30 m, cimientos de hormigón armado e instalación de antenas.",
  },
};

export default function Infrastructure() {
  const { lang } = useLang();
  const c = HERO_COPY[lang] || HERO_COPY.pt;
  useSeo(SEO[lang] || SEO.pt);

  return (
    <div className="relative min-h-screen bg-[#a8c8e2] text-slate-900">
      <Header />

      {/* Hero */}
      <section className="relative isolate overflow-hidden border-b border-slate-200">
        {/* Background tower photo: covers the entire hero (full width, full
            height) so the logo, nav and title appear directly over it. */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <img
            src="/towers/tower-30m.png"
            alt=""
            aria-hidden
            loading="eager"
            className="h-full w-full object-cover object-center"
          />
          {/* Subtle top fade to keep the navigation legible without hiding
              the sky portion of the photo. */}
          <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#d6e6f2]/55 to-transparent" />
          {/* Soft left fade so the title remains readable over the tower. */}
          <div className="absolute inset-0 bg-gradient-to-r from-white/35 via-white/10 to-transparent" />
        </div>
        <div className="mx-auto max-w-[1400px] px-6 pb-20 pt-12 md:px-12 md:pt-16 lg:px-16 lg:pb-24 lg:pt-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white/70 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-blue-700 shadow-sm backdrop-blur">
            {c.overline}
          </div>
          <h1
            data-testid="infra-title"
            className="font-heading mt-10 max-w-4xl text-3xl font-black leading-[0.95] tracking-tighter text-slate-900 sm:text-4xl md:text-5xl lg:text-6xl"
          >
            {c.title}
          </h1>
          <p
            className="mt-6 max-w-2xl rounded-lg bg-slate-900/55 px-4 py-3 text-sm font-semibold leading-relaxed text-white shadow-lg backdrop-blur-sm md:text-base"
            style={{ textShadow: "0 1px 3px rgba(0,0,0,0.45)" }}
          >
            {c.subtitle}
          </p>
          <p
            className="mt-4 inline-block rounded-md bg-amber-400/95 px-3 py-1.5 font-mono text-[11px] font-black uppercase tracking-[0.22em] text-slate-900 shadow-sm"
          >
            {c.badge}
          </p>
        </div>
      </section>

      {/* Towers showcase — full content with title "Torres de Telecomunicações e Mastros" */}
      <TowersShowcase showDetailCta={false} />

      {/* Backstage gallery — real photos of TVCabSat field operations */}
      <BackstageGallery />

      <Footer />
      <WhatsAppFab />
    </div>
  );
}
